import type { Integration as SharedIntegration } from '@gofranz/common-components';
import {
  HmacSecretReveal,
  IntegrationForm,
  type IntegrationFormSubmitPayload,
  useLanguageAwareRouting,
} from '@gofranz/common-components';
import { Alert, Button, Container, Group, Loader, Stack } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconKey, IconRefresh } from '@tabler/icons-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { PageHeader } from '../../../components/Common/PageHeader';
import { useRustyState } from '../../../state';

/**
 * Edit page for a single integration. Wraps the shared `IntegrationForm` and
 * adds a "rotate HMAC secret" affordance for adapters that support signing.
 *
 * The freshly-rotated secret is rendered via `HmacSecretReveal` in a banner
 * above the form — same one-time-display pattern as create.
 */
export function AccountIntegrationViewPage() {
  const { t } = useTranslation();
  const { api } = useRustyState.getState();
  const { uuid } = useParams<{ uuid: string }>();
  const nav = useNavigate();
  const { createLanguageURL } = useLanguageAwareRouting();

  const [integration, setIntegration] = useState<SharedIntegration | null>(null);
  const [rotatedSecret, setRotatedSecret] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!uuid) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.getIntegration(uuid);
        if (cancelled) return;
        setIntegration({
          id: res.id,
          user_id: res.user_id,
          title: res.title,
          kind: res.kind,
          config: res.config,
          hmac_secret_b64: undefined, // not returned on read
          created_at: res.created_at,
          updated_at: res.updated_at,
        });
      } catch (e) {
        console.error('Failed to load integration', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, uuid]);

  const handleSubmit = useCallback(
    async (payload: IntegrationFormSubmitPayload): Promise<SharedIntegration> => {
      if (!uuid) throw new Error('missing integration id');
      const updated = await api.updateIntegration(uuid, {
        title: payload.title,
        config: payload.config,
        // Only send `secrets` if the user actually entered something — empty
        // object means "no change", matching the backend's PATCH semantics.
        secrets:
          payload.secrets && Object.keys(payload.secrets).length > 0 ? payload.secrets : undefined,
      });
      notifications.show({
        color: 'green',
        title: t('formshive.integrations.updateSuccess.title'),
        message: t('formshive.integrations.updateSuccess.body'),
      });
      return {
        id: updated.id,
        user_id: updated.user_id,
        title: updated.title,
        kind: updated.kind,
        config: updated.config,
        created_at: updated.created_at,
        updated_at: updated.updated_at,
      };
    },
    [api, uuid, t]
  );

  const handleCancel = useCallback(() => {
    nav(createLanguageURL('/account/integrations'));
  }, [nav, createLanguageURL]);

  const handleRotate = useCallback(() => {
    if (!uuid) return;
    modals.openConfirmModal({
      title: t('formshive.integrations.rotateModal.title'),
      children: t('formshive.integrations.rotateModal.body'),
      labels: {
        confirm: t('formshive.integrations.rotateModal.confirm'),
        cancel: t('integrations.actions.cancel'),
      },
      confirmProps: { color: 'yellow' },
      onConfirm: async () => {
        try {
          const res = await api.rotateIntegrationSecret(uuid);
          setRotatedSecret(res.hmac_secret_b64);
        } catch (e) {
          console.error('Failed to rotate secret', e);
        }
      },
    });
  }, [api, uuid, t]);

  if (loading) {
    return (
      <Container fluid>
        <Group justify="center" py="xl">
          <Loader />
        </Group>
      </Container>
    );
  }

  if (!integration) {
    return (
      <Container fluid>
        <Alert color="red">{t('formshive.integrations.notFound')}</Alert>
      </Container>
    );
  }

  return (
    <Container fluid>
      <Stack gap="md">
        <PageHeader
          title={t('formshive.integrations.editPage.title')}
          subtitle={integration.title}
          action={
            <Button
              variant="light"
              color="yellow"
              leftSection={<IconRefresh size={16} />}
              onClick={handleRotate}
            >
              {t('formshive.integrations.editPage.rotateSecret')}
            </Button>
          }
        />
        {rotatedSecret && (
          <Alert color="yellow" icon={<IconKey size={18} />}>
            <HmacSecretReveal secret={rotatedSecret} />
          </Alert>
        )}
        <IntegrationForm
          mode="edit"
          initialValues={integration}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
        />
      </Stack>
    </Container>
  );
}
